<script setup lang="ts">
import { EntityWithCommentType, TaskItemDto } from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import TaskAdministrativeTab from '@/views/tasks/components/TaskAdministrativeTab.vue';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import TaskConfigurationTab from './TaskConfigurationTab.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import { useDeleteTaskWithConfirmation } from '../utils';
import { useFormTab } from '@/components/forms/useFormTab';

const router = useRouter();
const values = useFormValues<TaskItemDto>();

const mode = useFormMode();
const { t } = useTranslation();

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        title: t('AdministrativeInformation'),
        indicatorState: getIndicatorState(['name', 'attachments']),
      },

      configuration: {
        title: t('Configuration'),
        hideContentSheet: true,
        indicatorState: getIndicatorState(['taskItemAreas']),
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
    }) satisfies TabDefinition,
);

const { action: deleteAction } = useDeleteTaskWithConfirmation();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);
  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.tasks.edit,
        params: { id: values.value.id },
        hash: `#${TAB_PREFIX}configuration`,
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
            {
              ...(values.value as EntityInfo),
              typeName: 'Task_one',
            },
            t,
          );

          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: {
              ...defaultConfirmationOptions,
              size: 'large',
              contentData: {
                ...defaultConfirmationOptions.contentData,
                hint: t('DeleteConfigurationInfoMsg', {
                  hint: defaultConfirmationOptions.contentData.hint,
                }),
              },
            },
          });
          if (result?.success) {
            router.push({ name: routenames.tasks.overview });
          }
        }
      },
    },
  ];
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <TaskAdministrativeTab />
    </template>

    <template #configuration>
      <TaskConfigurationTab />
    </template>

    <template #comments>
      <FormSection>
        <Comments
          v-if="values.id"
          :id="values.id"
          :entityWithCommentType="EntityWithCommentType.TaskItem"
          @change="commentsChanged"
        />
      </FormSection>
    </template>
  </BasePageNavigation>
</template>
